import { cn } from '@/lib/utils';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ButtonHTMLAttributes, forwardRef } from 'react';

/**
 * PMS button. The base is neutral (black & white); a single noyau carries the
 * meaning — bleu-nuit for the primary action, rouge for destructive. Light-only,
 * 44px min touch target (WCAG 2.5.5), 8px radius, Titillium 600, 150ms ease.
 *
 * Variant names follow the shadcn convention (default/outline/ghost/destructive)
 * so the component API stays familiar; the visuals follow the PMS spec.
 */
const buttonVariants = cva(
  'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md border-2 font-semibold leading-none tracking-[0.005em] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40',
  {
    variants: {
      variant: {
        // Solid primary action (PMS "secondary" — plein bleu nuit).
        default:
          'border-bleu-nuit bg-bleu-nuit text-white hover:border-bleu-nuit-deep hover:bg-bleu-nuit-deep',
        // Outlined primary (PMS "primary" — bordure bleu nuit, fills on hover).
        outline: 'border-bleu-nuit bg-white text-bleu-nuit hover:bg-bleu-nuit hover:text-white',
        // No visual weight until hover.
        ghost: 'border-transparent bg-transparent text-bleu-nuit hover:bg-gray-100',
        // Irreversible action.
        destructive: 'border-rouge bg-white text-rouge hover:bg-rouge hover:text-white',
      },
      size: {
        default: 'h-11 px-5 text-base',
        sm: 'h-9 px-4 text-sm',
        lg: 'h-12 px-6 text-lg',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
export type { ButtonProps };
